"use client";

import { ChevronRight } from "lucide-react";
import Link from "next/link";
import React from "react";
import { useLocale, useTranslations } from "next-intl";

interface BreadcrumbItem {
  href?: string;
  name?: string;
  label?: string;
}

interface BreadcrumbProps {
  items: Array<BreadcrumbItem>;
  className?: string;
  separator?: "chevron" | "gt";
}

export function Breadcrumb({
  items,
  className = "",
  separator = "gt",
}: BreadcrumbProps) {
  const t = useTranslations("breadcrumb");
  const locale = useLocale();

  // 添加防护措施，确保 items 存在且为数组
  if (!items || !Array.isArray(items) || items.length === 0) {
    return (
      <nav
        className={`flex items-center space-x-2 ${className}`}
        aria-label="Breadcrumb"
      >
        <div className="flex items-center">
          <Link
            href={`/${locale}`}
            className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            {t("home")}
          </Link>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={`flex items-center space-x-2 ${className}`}
      aria-label="Breadcrumb"
    >
      {items.map((item, index) => {
        // 确保item有有效的数据
        if (!item) {
          return null;
        }

        // 获取显示文本，优先使用翻译键
        const displayText = item.label ? t(item.label) : item.name || "";
        const isLast = index === items.length - 1;

        return (
          <div
            key={`${item.href || item.label || item.name}-${index}`}
            className="flex items-center"
          >
            {index > 0 &&
              (separator === "chevron" ? (
                <ChevronRight
                  className="h-4 w-4 text-gray-400 dark:text-gray-500 mx-2"
                  aria-hidden="true"
                />
              ) : (
                <span className="text-gray-400 dark:text-gray-500 mx-2">
                  {">"}
                </span>
              ))}
            {item.href && !isLast ? (
              <Link
                href={item.href}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                {displayText}
              </Link>
            ) : (
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {displayText}
              </span>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export const BreadcrumbList = ({ children }: { children: React.ReactNode }) => (
  <ol>{children}</ol>
);
export const BreadcrumbItem = ({ children }: { children: React.ReactNode }) => (
  <li>{children}</li>
);

export const BreadcrumbLink = ({
  children,
  href,
}: {
  children: React.ReactNode;
  href: string;
}) => <Link href={href}>{children}</Link>;
// Removed BreadcrumbSeparator to avoid conflicts - use ChevronRight instead
export const BreadcrumbPage = ({ children }: { children: React.ReactNode }) => (
  <span>{children}</span>
);
